// Pure helpers for the camp roster import panel. Kept out of the component so
// the counting and gating rules are unit-tested — src/lib/* is the tested
// layer in this codebase, components are not.
//
// Everything here mirrors backend/app/routers/camp_import.py and
// backend/app/services/camp_import/*. Where the two could drift, the backend
// file and line that settles it is named in a comment.

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// ── outcomes ────────────────────────────────────────────────
//
// camp_import.py:82 `_OUTCOMES` — the preview response's `counts` object always
// carries all seven, whatever the sheet contained.
export const OUTCOME_ORDER = [
  'create', 'reuse', 'review', 'conflict', 'dup_in_file', 'error', 'skipped',
];

export const OUTCOME_LABELS = {
  create: 'New account',
  reuse: 'Existing account',
  review: 'Needs review',
  conflict: 'Blocked',
  dup_in_file: 'Duplicate in file',
  error: 'Error',
  skipped: 'Skipped',
};

// Outcomes that actually write something on commit.
// commit.py:507 `_WRITABLE_OUTCOMES`.
const IMPORTABLE = ['create', 'reuse', 'review'];

// ── warnings ────────────────────────────────────────────────
//
// Two different shapes reach this renderer:
//   row warnings   — prefixed codes ("suppressed:bounce_permanent",
//                    "typo_domain:gmail.com") or bare codes.
//   batch warnings — whole prose sentences from classify.batch_warnings, built
//                    from counts at request time and not enumerable here.
// Anything unrecognised is therefore returned verbatim, which is right for the
// prose and readable for a code we have not mapped yet.
const WARNING_LABELS = {
  shared_contact_different_name:
    'Same email or phone as an existing athlete with a different name — likely a sibling.',
  possible_duplicate: 'Looks like an athlete who is already in ProZpects.',
  unknown_position: 'Position not recognized for this sport; it will be left blank.',
};

export function warningLabel(code) {
  if (!code) return '';
  // hasOwnProperty, not a truthiness test: a bare lookup would answer
  // "constructor" and "toString" from Object.prototype, and batch warnings are
  // arbitrary strings rather than a closed vocabulary.
  if (has(WARNING_LABELS, code)) return WARNING_LABELS[code];
  if (code.startsWith('typo_domain:')) {
    return `Email domain may be a typo — did they mean ${code.slice('typo_domain:'.length)}?`;
  }
  if (code.startsWith('suppressed:')) {
    // notify.py:189 really does exclude these addresses from the send, so this
    // is a statement of fact and not a warning about a risk.
    return 'This address previously bounced or complained; it will not be emailed.';
  }
  return code;
}

// ── counting ────────────────────────────────────────────────

export function summarize(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const counts = {};
  OUTCOME_ORDER.forEach((outcome) => { counts[outcome] = 0; });
  list.forEach((row) => {
    const outcome = row && row.outcome;
    if (!outcome) return;
    counts[outcome] = (has(counts, outcome) ? counts[outcome] : 0) + 1;
  });
  return {
    ...counts,
    total: list.length,
    // The CEILING, not the forecast: review rows are counted here because they
    // COULD be imported, before the admin has decided anything. What will
    // actually be written is willImportCount(), which is what the UI must show
    // — see its comment.
    importable: list.filter((r) => r && IMPORTABLE.includes(r.outcome)).length,
  };
}

// The outcomes the server writes into the not-imported download
// (camp_import.py: download_errors). Kept in step with it deliberately: the
// modal offers that file, so counting a different set would describe it wrong.
const NOT_IMPORTED = ['error', 'conflict', 'dup_in_file', 'skipped'];

// How many rows of a finished batch did NOT become a registration.
//
// Drives whether the post-import modal appears at all. Skipped rows count —
// they are the ones needing follow-up, and leaving them out of the old errors
// download is exactly why six athletes on a real roster had to be recovered
// with a database query.
export function notImportedRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((r) => r && NOT_IMPORTED.includes(r.outcome)).length;
}

// How many rows a commit RIGHT NOW would actually write.
//
// `summarize().importable` counts every review row, including the ones the
// admin explicitly chose to Skip — so a two-sibling file read "Import 2 rows"
// and imported zero, and the typed >100 confirmation asked for a number that
// did not describe the operation. A review row only writes when it is resolved
// to `link`; commit.py turns skip, and anything it does not honor, into
// `skipped / unresolved_review`.
export function willImportCount(rows, state) {
  const resolved = resolutionPayload(state);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!row || !IMPORTABLE.includes(row.outcome)) return false;
    if (row.outcome !== 'review') return true;
    const choice = resolved[String(row.row_number)];
    return Boolean(choice) && choice.action === 'link';
  }).length;
}

export function needsResolution(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => row && row.outcome === 'review');
}

// ── resolutions ─────────────────────────────────────────────
//
// THE ONLY TWO ACTIONS COMMIT HONORS. commit.py:608-625 accepts `skip`, and
// `link` WITH a user_id; on a review row every other value — including
// "create" — is turned into `skipped / unresolved_review` and nothing is
// written. That is deliberate and covered by
// test_commit_batch_skips_a_review_row_with_an_unsupported_resolution: a review
// row's contact already belongs to somebody (under the shared-inbox rule, to
// the sibling), so anything that is not an explicit link would fall through to
// contact matching and attach this child's registration to their brother.
//
// The panel must therefore offer exactly these two. Offering "create a new
// account" would let an admin resolve a whole review queue, pass the gate
// below, and have every one of those rows silently skipped.
export const RESOLUTION_ACTIONS = ['link', 'skip'];

export const RESOLUTION_LABELS = {
  link: 'Link to an existing athlete',
  skip: 'Skip this row',
};

// UI state is keyed by row number: { 5: { action: 'link', userId: 1953 } }.
// A 'link' with no chosen account is incomplete and is dropped rather than
// sent, so the server never has to guess what the admin meant — and so is any
// action the server would not honor, so that canCommit keeps the button
// disabled instead of promising an import that turns into a silent skip.
export function resolutionPayload(state) {
  const payload = {};
  Object.entries(state || {}).forEach(([rowNumber, choice]) => {
    const action = choice && choice.action;
    if (action === 'skip') {
      payload[rowNumber] = { action: 'skip' };
      return;
    }
    if (action === 'link' && choice.userId) {
      payload[rowNumber] = { action: 'link', user_id: choice.userId };
    }
  });
  return payload;
}

// ── gating ──────────────────────────────────────────────────

const CONFIRM_ABOVE_ROWS = 100;

export function confirmationRequired(count) {
  return Number(count) > CONFIRM_ABOVE_ROWS;
}

export function canCommit(preview, state) {
  if (!preview || !Array.isArray(preview.rows)) return false;
  // willImportCount, not summarize().importable: a file whose only importable
  // rows are review rows the admin skipped writes nothing, and the button must
  // not offer to "Import 0 rows".
  if (willImportCount(preview.rows, state) === 0) return false;
  const resolved = resolutionPayload(state);
  return needsResolution(preview.rows).every((row) => has(resolved, String(row.row_number)));
}

// ── send estimate ───────────────────────────────────────────
//
// notify.py:70 `_NOTIFIABLE_OUTCOMES` — every other outcome wrote nobody and
// has nobody to message.
const NOTIFIABLE_OUTCOMES = ['create', 'reuse'];

const isSuppressed = (row) => (row.warnings || []).some(
  (code) => typeof code === 'string' && code.startsWith('suppressed:'));

// What a commit WOULD send, estimated from the preview.
//
// BOTH NUMBERS ARE APPROXIMATE AND THE UI MUST NOT PRESENT EITHER AS EXACT.
// The preview response carries no recipient counts and no phone column
// (camp_import.py `_row_out`), so this cannot be read off the API — and it
// cannot be made exact here either, because notify.dedupe_destinations keys on
// the COMMITTED ACCOUNT's address (notify.py:175-190) while all a preview has
// is the SHEET's:
//
//   * a `reuse` row matched by PHONE with a blank Email column counts 0 here
//     and 1 there — the one case that UNDER-states a send;
//   * two rows whose sheet emails differ but which resolve to one account count
//     2 here and 1 there;
//   * suppression is tested against the account's address, so a `suppressed:*`
//     warning is a good signal rather than the same test.
//
// SMS is a plain upper bound and is deliberately left that way: the phone leg
// keys on the parent's E.164 number, so two siblings sharing one phone are one
// message there and two here. Over-stating what an admin is about to authorise
// is the safe direction to be wrong in.
//
// The exact figures exist only AFTER a commit, as `pending` on
// GET /import/batches/{id} — which is what the send-later buttons show.
export function estimateNotify(rows, state) {
  const resolved = resolutionPayload(state);
  const emails = new Set();
  let sms = 0;
  (rows || []).forEach((row) => {
    if (!row) return;
    let outcome = row.outcome;
    if (outcome === 'review') {
      const choice = resolved[String(row.row_number)];
      // Unresolved or skipped: commit writes nobody, so there is nobody to
      // message. A linked row lands on an account that already exists, which is
      // a "reuse" as far as notification goes.
      if (!choice || choice.action !== 'link') return;
      outcome = 'reuse';
    }
    if (!NOTIFIABLE_OUTCOMES.includes(outcome)) return;
    const email = (row.email || '').trim().toLowerCase();
    if (email && !isSuppressed(row)) {
      emails.add(email);
      return;
    }
    // No inbox we can reach. commit mints a managed placeholder address and
    // notify falls through to the phone leg — but only for rows that actually
    // mint an account (notify.py: `record.created_user and phone`).
    if (outcome === 'create') sms += 1;
  });
  return { email: emails.size, sms };
}

// ── timestamps ──────────────────────────────────────────────

// Every timestamp the import API returns is `datetime.utcnow().isoformat()` —
// UTC with NO offset (camp_import.py:442-448). `new Date('2026-08-04T15:30:00')`
// reads a bare datetime as LOCAL time, which shifts an import's "uploaded at"
// by the browser's offset. Append the Z the serializer left off. Date-only
// values already parse as UTC midnight per the spec and must NOT be touched.
// Same rule as conciergeBoardModel._ms.
const HAS_OFFSET = /[zZ]|[+-]\d{2}:?\d{2}$/;

export function parseApiTimestamp(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  const needsZ = text.includes('T') && !HAS_OFFSET.test(text);
  const parsed = new Date(needsZ ? `${text}Z` : text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// ── undo ────────────────────────────────────────────────────

// undo.py:68-69 `_UNDOABLE_REFUSALS`, plus the two short-circuits above it.
const UNDO_REFUSALS = {
  batch_not_found: 'That import no longer exists.',
  already_undone: 'This import has already been undone.',
  nothing_committed: 'This import was never committed, so there is nothing to undo.',
  commit_in_progress: 'This import is still importing. Wait for it to finish, then undo.',
};

// undo.py:72-151 `undo_reasons`, plus `_registration_skip_reason` and the
// transient `delete_failed`. Each one is why an account outlived the rollback.
const UNDO_SKIP_REASONS = {
  already_gone: 'The account had already been removed.',
  has_logged_in: 'They have logged in.',
  password_set: 'They set a password.',
  account_claimed: 'They claimed the account.',
  has_measurables: 'They have measurables recorded.',
  has_camp_photos: 'They have camp photos.',
  has_verifications: 'Their stats have been verified.',
  checked_in: 'They checked in at this camp.',
  registered_for_another_camp: 'They are registered for another camp.',
  guardian_of_other_athletes: 'They are the guardian of another athlete.',
  registration_not_created_by_import: 'This import did not create the registration.',
  registration_created_by_another_import: 'An earlier import created the registration.',
  delete_failed: 'The account could not be removed.',
};

export function undoRefusalLabel(code) {
  if (!code) return '';
  return has(UNDO_REFUSALS, code) ? UNDO_REFUSALS[code] : code;
}

export function undoSkipReasonLabel(reason) {
  if (!reason) return '';
  return has(UNDO_SKIP_REASONS, reason) ? UNDO_SKIP_REASONS[reason] : reason;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Shape the /import/batches/{id}/undo response for display.
//
// `retryable` is the subtle one. undo.py:395-399 leaves the batch un-finalized
// when a delete failed, so the run can be repeated — but a repeat only helps if
// the admin can clear whatever blocked the delete, and some blocks (an athlete
// who is another account's payer, say) never clear. The copy therefore offers
// the retry without claiming it will work.
export function undoSummary(result) {
  if (!result || typeof result !== 'object') return null;

  const counts = {
    removed: result.removed || 0,
    guardiansRemoved: result.guardians_removed || 0,
    registrationsRemoved: result.registrations_removed || 0,
    notifiedRemoved: result.notified_removed || 0,
  };
  const skipped = (result.skipped || []).map((entry) => ({
    ...entry,
    reasonLabels: (entry.reasons || []).map(undoSkipReasonLabel),
  }));

  if (result.refused) {
    return {
      state: 'refused',
      headline: undoRefusalLabel(result.refused),
      notes: [],
      counts,
      skipped,
      canRetry: false,
    };
  }

  const notes = [];
  if (counts.notifiedRemoved > 0) {
    notes.push(`${plural(counts.notifiedRemoved, 'removed athlete', 'removed athletes')} `
      + 'had already been emailed or texted. That cannot be taken back.');
  }
  if (skipped.length > 0) {
    notes.push(`${plural(skipped.length, 'account was', 'accounts were')} kept.`);
  }

  if (result.retryable) {
    return {
      state: 'incomplete',
      // NOT "nothing was undone": every successful row delete was already
      // committed (undo.py:245-247). What was left alone is the batch STATUS,
      // which is what makes a second run possible.
      headline: 'Undo did not finish. The batch was left as it was, so you can run it again.',
      notes: notes.concat(
        'Running it again may not clear the problem — some accounts cannot be removed.'),
      counts,
      skipped,
      canRetry: true,
    };
  }

  return {
    state: 'undone',
    headline: 'Undo complete.',
    notes,
    counts,
    skipped,
    canRetry: false,
  };
}
